import {
  Autocomplete,
  Chip,
  TextField,
  createFilterOptions,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useField } from "formik";
import { isString } from "lodash-es";

const filter = createFilterOptions<TagOption>();

export type TagType = "CUSTOM" | "SYSTEM";
export type TagOption = { type: TagType; value: string; inputValue?: string };

export interface TagsFieldProps {
  name: string;
  options: string[];
  // reservedOptions: string[];
  inputLabel: string;
  inputPlaceholder?: string;
  required?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: string;
  noOptionsText?: string;
  getTagLabel?: (tag: string) => string | undefined;
}

export const TagsField = ({
  name,
  disabled,
  required,
  inputLabel,
  inputPlaceholder,
  options,
  // reservedOptions,
  autoFocus,
  noOptionsText,
  getTagLabel,
}: TagsFieldProps) => {
  const { t } = useTranslate(["Global"]);
  const [, { value, touched, error }, { setValue, setTouched }] =
    useField<TagOption[]>(name);

  // const { reservedTagLabels } = useReservedTagLabels();

  // const autocompleteOptions = options.map(o => ({
  //   value: o,
  //   label: isTagReserved(o) ? reservedTagLabels[o] : o,
  // }));

  return (
    <Autocomplete
      value={value}
      freeSolo
      multiple
      fullWidth
      openOnFocus
      clearOnBlur
      autoHighlight
      handleHomeEndKeys
      selectOnFocus={true}
      autoFocus={autoFocus}
      noOptionsText={noOptionsText}
      options={options.map(option => ({
        type: "CUSTOM" as const,
        value: option,
      }))}
      getOptionLabel={(o: TagOption | string) =>
        typeof o === "string" ? o : o.value
      }
      isOptionEqualToValue={(option: TagOption, value: TagOption | string) =>
        option.value === (typeof value === "string" ? value : value.value)
      }
      disabled={disabled}
      renderInput={params => (
        <TextField
          {...params}
          name={name}
          label={inputLabel}
          placeholder={inputPlaceholder}
          disabled={disabled}
          required={required}
          error={touched && Boolean(error)}
          helperText={touched && isString(error) ? error : undefined}
        />
      )}
      // @ts-ignore
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        if (params.inputValue !== "") {
          filtered.push({
            inputValue: params.inputValue,
            value: t("Add “{name}”", {
              ns: "Global",
              name: params.inputValue.trim().toLocaleUpperCase(),
            }),
            type: "CUSTOM",
          });
        }
        return filtered;
      }}
      renderValue={(value, getTagProps) =>
        value.map((value, index) => {
          const { onDelete, key, ...tagProps } = getTagProps({ index });

          return typeof value === "string" ? (
            <Chip
              key={value}
              variant="outlined"
              size="small"
              label={getTagLabel?.(value) ?? value}
              onDelete={onDelete}
              {...tagProps}
            />
          ) : (
            <Chip
              key={value.value}
              variant="outlined"
              size="small"
              color={value.type === "SYSTEM" ? "primary" : undefined}
              label={getTagLabel?.(value.value) ?? value.value}
              onDelete={value.type === "SYSTEM" ? undefined : onDelete} // we don't allow to remove system tags
              {...tagProps}
            />
          );
        })
      }
      onChange={async (e: any, newValue: (TagOption | string)[]) => {
        const newTags = newValue
          .map(v =>
            typeof v === "string"
              ? { type: "CUSTOM" as const, value: v }
              : "inputValue" in v && v.inputValue
                ? { type: "CUSTOM" as const, value: v.inputValue }
                : v
          )
          .map(t => ({ ...t, value: t.value.toLocaleUpperCase().trim() }))
          .filter(t => t.value !== "");
        await setValue(newTags);
        await setTouched(true);
      }}
      disableCloseOnSelect
      disableClearable
    />
  );
};
